import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PRODUCTS, COLLECTIONS } from '../mock';

const Wheels = () => {
  const wheelsCollection = useMemo(
    () => COLLECTIONS.find((c) => c.id === 'fortune-forged-wheels'),
    []
  );
  const wheels = useMemo(
    () => PRODUCTS.filter((p) => p.collectionId === 'fortune-forged-wheels'),
    []
  );

  return (
    <div className="bg-white text-neutral-900 min-h-screen">
      {/* Hero */}
      <section
        className="relative pt-40 pb-24 lg:pt-48 lg:pb-32 text-white"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.85), rgba(0,0,0,0.55)), url(${wheelsCollection?.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">
            FORTUNE FORGED&trade;
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight max-w-3xl">
            FORGED FOR THE TRACK.<br />
            <span className="text-[#FF6B00]">BUILT FOR YOU.</span>
          </h1>
          <p className="text-neutral-200 mt-6 max-w-2xl text-lg leading-relaxed">
            Bespoke forged wheels engineered for individuality, performance, and precision. Choose from
            12 designs, available in Monoblock (1-Piece) or 2-Piece construction &mdash; every set is
            made-to-order to your spec.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="border-b border-neutral-100 py-4">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-sm text-neutral-500">
          <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-neutral-900">Fortune Forged Wheels</span>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="font-display tracking-[0.3em] text-[10px] text-[#FF6B00] font-semibold mb-1">
                COLLECTION
              </div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight">
                {wheels.length} DESIGNS &middot; {wheels.length * 2 - 2} VARIANTS
              </h2>
            </div>
            <p className="text-sm text-neutral-500 max-w-md">
              From <span className="font-display text-neutral-900">$595 USD</span> &middot; Monoblock 1-Piece
              <br />
              From <span className="font-display text-neutral-900">$945 USD</span> &middot; 2-Piece Multi
            </p>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10"
            data-testid="wheels-product-grid"
          >
            {wheels.map((p) => {
              const variantCount = p.variants?.length || 1;
              return (
                <Link
                  key={p.id}
                  to={`/product/${p.slug}`}
                  data-testid={`wheel-card-${p.id}`}
                  className="group block"
                >
                  <div className="aspect-square bg-neutral-50 overflow-hidden border border-neutral-100 group-hover:border-neutral-300 transition-colors">
                    {p.images && p.images[0] && (
                      <img
                        src={p.images[0]}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-contain p-6 transform group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </div>
                  <div className="py-4 border-b-2 border-transparent group-hover:border-[#FF6B00] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display tracking-[0.22em] text-[10px] text-[#FF6B00] font-semibold mb-1">
                          FORTUNE BLACK LABEL
                        </div>
                        <h3 className="font-display text-xl tracking-wide group-hover:text-[#FF6B00] transition-colors truncate">
                          {p.name}
                        </h3>
                        <div className="text-sm text-neutral-700 mt-1">
                          From <span className="text-neutral-900 font-display">${p.price.toLocaleString()}</span> USD
                        </div>
                        <div className="text-xs text-neutral-500 mt-1">
                          {variantCount > 1 ? 'Monoblock & 2-Piece available' : 'Monoblock only'}
                        </div>
                      </div>
                      <ArrowRight
                        size={18}
                        className="text-neutral-400 group-hover:text-[#FF6B00] group-hover:translate-x-1 transition-all mt-1 flex-shrink-0"
                      />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-[#FF6B00] py-16 mt-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center text-white">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-4">
            READY TO START YOUR BUILD?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-7">
            Every Fortune Forged set is made-to-order. Reach out to book a consultation and we&rsquo;ll
            tailor fitment, finish, and lead time to your build.
          </p>
          <Link
            to="/contact"
            data-testid="wheels-cta-contact"
            className="inline-block bg-black hover:bg-neutral-900 transition-colors px-8 py-4 font-display tracking-[0.22em] text-sm font-bold"
          >
            CONTACT OUR TEAM
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Wheels;
